import { useMemo } from 'react';
import { VegaEmbed } from 'react-vega';
import { expressionInterpreter } from 'vega-interpreter';
import { TopLevelSpec } from 'vega-lite';

import { Classification } from '@/types/classification';

import { ChartTheme, SortBy } from './types';

interface Props {
  activeOnly: boolean;
  classifications: Classification[];
  // Key of the badge the operator clicked, dimming every other bar.
  highlightedKey: string | null;
  // Number of days of history to show, or null for all time.
  rangeDays: number | null;
  sortBy: SortBy;
  theme: ChartTheme;
}

// Vega bakes colours into the SVG it renders, so the axes and labels can't pick
// the theme up from CSS -- without this the chart draws dark ink on a dark page.
// Values are the neutrals each theme already uses for muted and body text.
const AXIS_PALETTES: Record<
  Exclude<ChartTheme, 'light'>,
  { ink: string; axis: string; grid: string }
> = {
  // uniform-gray 300 / 500 / 700, against the #1B2128 classic dark page.
  classic: { ink: '#A3B1C1', axis: '#677D98', grid: '#3E4B5C' },
  // The midnight equivalents, against #0d1117.
  midnight: { ink: '#bcc5cf', axis: '#6b7683', grid: '#232b35' }
};

// A key can appear more than once over time, so the date sort aggregates its
// rows by earliest start rather than ordering by whichever one vega happens to
// reach first.
const SORT_SPECS = {
  active_from: { field: 'active_from', op: 'min', order: 'ascending' },
  key: 'ascending'
} as const;

// Pushed well below the old 0.25 so the selected bar is the only thing that
// reads at a glance rather than merely the brightest of a legible set.
const DIMMED_OPACITY = 0.12;

// Mid grey so the outline holds up against both the light and dark chart
// backgrounds without the spec needing to know the theme.
const HIGHLIGHT_STROKE = '#6b7280';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const ClassificationsChart = ({
  activeOnly,
  classifications,
  highlightedKey,
  rangeDays,
  sortBy,
  theme
}: Props) => {
  const { spec, hasVisibleClassifications } = useMemo(() => {
    const now = new Date();
    const cutoff = rangeDays === null ? null : new Date(now.getTime() - rangeDays * MS_PER_DAY);

    const values = classifications
      // An absent active_until means the classification is still in effect.
      .filter((c) => !activeOnly || c.active_until === null)
      .map((c) => ({
        key: c.key,
        active_from: c.active_from,
        active_until: c.active_until ?? now.toISOString(),
        opacity: highlightedKey === null || c.key === highlightedKey ? 1 : DIMMED_OPACITY,
        // A stroke only on the selected bars gives the highlight an edge, so it
        // stands out even where a colour happens to be pale.
        strokeWidth: c.key === highlightedKey ? 2 : 0
      }))
      // A classification belongs in the window if it was still active at any
      // point inside it, even when it started before the cutoff.
      .filter((v) => cutoff === null || new Date(v.active_until) >= cutoff);

    // Clamping the axis rather than the values keeps bars that start before the
    // cutoff visible, just trimmed at the left edge.
    const scale = cutoff === null ? undefined : { domain: [cutoff.getTime(), now.getTime()] };

    const sort = SORT_SPECS[sortBy];

    const palette = theme === 'light' ? null : AXIS_PALETTES[theme];
    const config = {
      font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      ...(palette && {
        background: 'transparent',
        // Vega's default view border is near-black and frames the plot area.
        view: { stroke: 'transparent' },
        axis: {
          labelColor: palette.ink,
          titleColor: palette.ink,
          // The axis line itself carries more meaning than the gridlines, so
          // it gets the stronger of the two neutrals.
          domainColor: palette.axis,
          tickColor: palette.axis,
          gridColor: palette.grid
        }
      })
    };

    return {
      hasVisibleClassifications: values.length > 0,
      spec: {
        width: 'container',
        config,
        mark: { type: 'rect', clip: true, stroke: HIGHLIGHT_STROKE },
        data: { values },
        encoding: {
          x: { field: 'active_from', type: 'temporal', title: 'From', scale },
          x2: { field: 'active_until' },
          y: { field: 'key', type: 'nominal', title: 'Classification', sort },
          color: { field: 'key', type: 'nominal', legend: null },
          // Passing the opacity straight through from the data avoids an
          // expression the CSP-safe interpreter would have to evaluate.
          opacity: { field: 'opacity', type: 'quantitative', scale: null, legend: null },
          strokeWidth: { field: 'strokeWidth', type: 'quantitative', scale: null, legend: null },
          tooltip: [
            { field: 'key', type: 'nominal', title: 'Classification' },
            { field: 'active_from', type: 'temporal', title: 'From', format: '%Y-%m-%d' },
            { field: 'active_until', type: 'temporal', title: 'Until', format: '%Y-%m-%d' }
          ]
        }
      } as TopLevelSpec
    };
  }, [activeOnly, classifications, highlightedKey, rangeDays, sortBy, theme]);

  if (!hasVisibleClassifications) {
    return (
      <p className="text-sm text-gray-500 mt-2">
        {classifications.length === 0
          ? 'No classification history available.'
          : 'No classifications match the selected filters.'}
      </p>
    );
  }

  return (
    <div className="w-full mt-2">
      <VegaEmbed
        spec={spec}
        className="w-full"
        options={{ ast: true, expr: expressionInterpreter }}
      />
    </div>
  );
};

export default ClassificationsChart;
